
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'UIInlineSwitcher',
  props: {
    options: {
      type: Array as PropType<string[]>,
      required: true,
      validator: (value: string[]) => Array.isArray(value) && value.length >= 2
    },
    value: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: 'Option selector'
    },
    orientation: {
      type: String as PropType<'horizontal' | 'vertical'>,
      default: 'horizontal'
    },
    loop: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    selectOption(option: string): void {
      if (!this.disabled && option !== this.value) {
        this.$emit('input', option)
      }
    },
    handleKeydown(event: KeyboardEvent, currentIndex: number): void {
      if (this.disabled) return

      let newIndex: number | null = null
      const lastIndex = this.options.length - 1
      const isHorizontal = this.orientation === 'horizontal'

      switch (event.key) {
        case 'ArrowRight':
          if (isHorizontal) {
            event.preventDefault()
            newIndex = this.getNextIndex(currentIndex, 1)
          }
          break
        case 'ArrowDown':
          if (!isHorizontal) {
            event.preventDefault()
            newIndex = this.getNextIndex(currentIndex, 1)
          }
          break
        case 'ArrowLeft':
          if (isHorizontal) {
            event.preventDefault()
            newIndex = this.getNextIndex(currentIndex, -1)
          }
          break
        case 'ArrowUp':
          if (!isHorizontal) {
            event.preventDefault()
            newIndex = this.getNextIndex(currentIndex, -1)
          }
          break
        case 'Home':
          event.preventDefault()
          newIndex = 0
          break
        case 'End':
          event.preventDefault()
          newIndex = lastIndex
          break
        case ' ':
        case 'Enter':
          event.preventDefault()
          this.selectOption(this.options[currentIndex])
          return
        default:
          break
      }

      if (newIndex !== null && newIndex !== currentIndex) {
        this.$emit('input', this.options[newIndex])
        this.$nextTick(() => {
          const buttons = this.$el.querySelectorAll('button')
          buttons[newIndex as number]?.focus()
        })
      }
    },
    getNextIndex(currentIndex: number, direction: number): number | null {
      const lastIndex = this.options.length - 1
      const nextIndex = currentIndex + direction

      if (this.loop) {
        return (nextIndex + this.options.length) % this.options.length
      }

      if (nextIndex < 0 || nextIndex > lastIndex) {
        return null
      }
      return nextIndex
    }
  }
})
